/**
 * Contact-form bot check (added 2026-08-31 after spam got past the honeypot).
 *
 * WHY: the honeypot only catches bots that fill every field they find. A bot
 * that actually parses the form walks straight through it. The cheap, reliable
 * discriminator for form spam is JavaScript: almost no spam bot executes page
 * JS — it scrapes the HTML once and POSTs the field names forever. So the
 * handler now REQUIRES a token that only this file can mint. That is a
 * deliberate reversal of the original "form works with JS disabled" rule; the
 * page carries a <noscript> block pointing at info@collegeoaks.net, and the
 * submit button ships disabled and is enabled here, so a no-JS visitor is told
 * to email rather than filling a form that would be rejected.
 *
 * The token is `v1.<nonce>.<dwell>.<interacted>.<sig>`:
 *   nonce       random hex, makes each submission unique (server rejects reuse,
 *               so one captured token can't be replayed in a loop)
 *   dwell       whole seconds between page load and submit, from
 *               performance.now() — monotonic, so a wrong device clock can't
 *               lock anyone out. The server wants a couple of seconds at least.
 *   interacted  1 once a real pointer/keyboard event lands in the form
 *   sig         FNV-1a checksum of the other three plus a shared salt
 *
 * The signature is OBFUSCATION, NOT SECURITY — the salt ships in this bundle,
 * so anyone reading it can forge a token. That is fine and by design: the point
 * is to make the form cost more to submit than a generic spam script will
 * spend, not to withstand a targeted attacker. contact_form.py on the droplet
 * implements the identical checksum; CHANGE BOTH OR NEITHER.
 */

const SALT = "collegeoaks-contact-v1"

// FNV-1a, 32-bit. Small, dependency-free, and trivial to mirror in Python.
// Math.imul keeps the multiply in 32-bit range the way the reference does.
function checksum(str) {
  let h = 0x811c9dc5
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i)
    h = Math.imul(h, 0x01000193) >>> 0
  }
  return h >>> 0
}

function randomNonce() {
  const bytes = new Uint8Array(6)
  crypto.getRandomValues(bytes)
  return [...bytes].map((b) => b.toString(16).padStart(2, "0")).join("")
}

export function initContactForm() {
  const form = document.querySelector("[data-contact-form]")
  if (!form) return

  const token = form.querySelector("[data-bot-token]")
  const submit = form.querySelector("[data-needs-js]")
  if (!token || !submit) return

  // JS is alive, so the form is usable. (It ships disabled — see the file
  // header — so no-JS visitors are pointed at email instead of a dead form.)
  submit.disabled = false
  submit.removeAttribute("data-needs-js")

  // performance.now() is monotonic from page load: immune to clock skew and to
  // the visitor's timezone, unlike Date.now().
  let interacted = 0
  const noteInteraction = () => {
    interacted = 1
  }
  form.addEventListener("pointerdown", noteInteraction, { once: true, passive: true })
  form.addEventListener("keydown", noteInteraction, { once: true })

  form.addEventListener("submit", () => {
    const nonce = randomNonce()
    const dwell = Math.max(0, Math.round(performance.now() / 1000))
    const sig = checksum(`${nonce}:${dwell}:${interacted}:${SALT}`)
    token.value = `v1.${nonce}.${dwell}.${interacted}.${sig}`
  })
}
